import React, { useState, useEffect, useRef } from 'react';
import { 
  Paper, 
  Group, 
  Stack, 
  Text, 
  ActionIcon, 
  TextInput,
  Button,
  Divider,
  SegmentedControl,
  Box,
  Modal,
  Title
} from '@mantine/core';
import { IconPencil, IconCheck, IconX, IconMapPin, IconSearch, IconLoader2, IconMapPinExclamation, IconMapPinQuestion } from '@tabler/icons-react';
import { ImageDetails } from '../../../../../types/Image';
import { ShoeboxLocation, Geolocation } from '../../../../../types/Location';
import { 
  generateMapboxStaticImageUrl, 
  hasGeocodableInformation, 
  geocodeLocation, 
  loadGoogleMapsScript,
  createPlaceAutocompleteElement,
  extractLocationFromPlace
} from '../../../../../utils/locationUtils';

interface LocationSectionProps {
  location?: ShoeboxLocation;
  eventLocation?: ShoeboxLocation;
  onSaveLocation?: (location: ShoeboxLocation) => void;
  onSaveEventLocation?: (location: ShoeboxLocation) => void;
  hasEvent?: boolean; // Flag indicating if the image is part of an event
}

export const LocationSection: React.FC<LocationSectionProps> = ({ 
  location, 
  eventLocation,
  onSaveLocation,
  onSaveEventLocation,
  hasEvent = false
}) => {
  const [editing, setEditing] = useState(false);
  // State for location fields
  const [locationSearch, setLocationSearch] = useState('');
  const [sublocation, setSublocation] = useState(location?.sublocation || '');
  const [address, setAddress] = useState(location?.address || '');
  const [streetAddress, setStreetAddress] = useState(location?.street_address || '');
  const [city, setCity] = useState(location?.city || '');
  const [state, setState] = useState(location?.state || '');
  const [country, setCountry] = useState(location?.country || '');
  
  // State for geocoding and map display
  const [mapImageUrl, setMapImageUrl] = useState<string | null>(null);
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const [geocodingError, setGeocodingError] = useState<string | null>(null);
  const [currentGeolocation, setCurrentGeolocation] = useState<Geolocation | null>(
    location?.geolocation || null
  );
  const [externalIdentifiers, setExternalIdentifiers] = useState<ShoeboxLocation['external_identifiers']>(
    location?.external_identifiers || []
  );
  const [isGoogleMapsLoading, setIsGoogleMapsLoading] = useState(false);
  
  // State for the apply-to-event modal
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<ShoeboxLocation | null>(null);
  
  // Refs for Google Maps integration
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const googleMapsLoadedRef = useRef(false);
  // Use any type to avoid TypeScript errors with the new API
  const placeAutocompleteRef = useRef<any>(null);
  
  // Initialize map image URL based on location or eventLocation
  useEffect(() => {
    // First check if image has location data (handle both null and undefined)
    const hasImageLocation = location !== null && location !== undefined;
    const hasEventLocation = eventLocation !== null && eventLocation !== undefined;
    
    if (hasImageLocation && location?.geolocation) {
      // Use coordinates from image location
      setCurrentGeolocation(location.geolocation);
      setMapImageUrl(generateMapboxStaticImageUrl(location.geolocation));
    } else if (hasImageLocation && hasGeocodableInformation(location)) {
      // Try to geocode the image location
      geocodeLocationFromMetadata(location);
    } 
    // If no image location but event has location, use that
    else if (!hasImageLocation && hasEventLocation && eventLocation?.geolocation) {
      // Use coordinates from event location
      setCurrentGeolocation(eventLocation.geolocation);
      setMapImageUrl(generateMapboxStaticImageUrl(eventLocation.geolocation));
      
      // Pre-fill the form fields with event location data
      setSublocation(eventLocation.sublocation || '');
      setAddress(eventLocation.address || '');
      setStreetAddress(eventLocation.street_address || '');
      setCity(eventLocation.city || '');
      setState(eventLocation.state || '');
      setCountry(eventLocation.country || '');
      setExternalIdentifiers(eventLocation.external_identifiers || []);
    } else if (!hasImageLocation && hasEventLocation && hasGeocodableInformation(eventLocation)) {
      // Try to geocode the event location
      geocodeLocationFromMetadata(eventLocation);
    }
  }, [location, eventLocation]);
  
  // This effect is now handled in the combined effect above
  
  // Initialize Google Places Autocomplete when editing mode changes
  useEffect(() => {
    if (editing) {
      // Load Google Maps API if not already loaded
      if (!googleMapsLoadedRef.current) {
        setIsGoogleMapsLoading(true);
        setGeocodingError(null);
        
        // Add a small delay before loading to ensure DOM is ready
        setTimeout(() => {
          loadGoogleMapsScript()
            .then(() => {
              googleMapsLoadedRef.current = true;
              setIsGoogleMapsLoading(false);
              
              // Add a small delay to ensure the API is fully initialized
              setTimeout(() => {
                if (searchContainerRef.current) {
                  initGooglePlacesAutocomplete();
                }
              }, 100);
            })
            .catch(error => {
              console.error('Failed to load Google Maps API:', error);
              setGeocodingError('Failed to load Google Maps API');
              setIsGoogleMapsLoading(false);
            });
        }, 100);
      } else if (searchContainerRef.current) {
        // If API is already loaded, just initialize the autocomplete
        initGooglePlacesAutocomplete();
      }
    }
  }, [editing]);
  
  // Geocode location from location data
  const geocodeLocationFromMetadata = async (locationToGeocode = location) => {
    if (!locationToGeocode || locationToGeocode === null) return;
    
    setIsGeocodingLoading(true);
    setGeocodingError(null);
    
    try {
      const result = await geocodeLocation(location);
      if (result) {
        setCurrentGeolocation(result);
        setMapImageUrl(generateMapboxStaticImageUrl(result));
      } else {
        setGeocodingError('Could not find coordinates for this location');
      }
    } catch (error) {
      console.error('Error geocoding location:', error);
      setGeocodingError('Error geocoding location');
    } finally {
      setIsGeocodingLoading(false);
    }
  };
  
  // Initialize Google Places Autocomplete using the modern PlaceAutocompleteElement
  const initGooglePlacesAutocomplete = () => {
    // Double-check that the API is loaded and the container exists
    if (!searchContainerRef.current) {
      console.error('Search container ref is not available');
      return;
    }
    
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error('Google Maps API not loaded properly');
      setGeocodingError('Google Maps API not loaded properly');
      return;
    }
    
    try {
      // Clear the container first
      searchContainerRef.current.innerHTML = '';
            // Create the modern PlaceAutocompleteElement
      if (typeof window.google.maps.places.PlaceAutocompleteElement?.create === 'function') {
        // Use the modern PlaceAutocompleteElement API
        console.log('Using modern PlaceAutocompleteElement API');
        
        try {
          // Create the element
          const placeAutocompleteElement = window.google.maps.places.PlaceAutocompleteElement.create({
            types: ['geocode', 'establishment'],
            fields: ['address_components', 'geometry', 'name', 'formatted_address', 'place_id'],
            appearance: 'inline-block'
          });
          
          // Add event listener for place selection
          placeAutocompleteElement.addEventListener('gmp-placeSelect', async () => {
            try {
              // Get the place details
              const place = await placeAutocompleteElement.getPlaceResult();
              if (place && place.geometry && place.geometry.location) {
                handlePlaceSelection(place);
              }
            } catch (error) {
              console.error('Error getting place result:', error);
            }
          });
          
          // Store reference and append to container
          placeAutocompleteRef.current = placeAutocompleteElement;
          // Cast to Node to avoid TypeScript errors
          searchContainerRef.current.appendChild(placeAutocompleteElement as unknown as Node);
        } catch (error) {
          console.error('Error creating PlaceAutocompleteElement:', error);
          setGeocodingError('Error initializing location search');
        }
      } else {
        // Fallback to legacy Autocomplete API with a warning
        console.warn('PlaceAutocompleteElement not available, falling back to legacy Autocomplete');
        
        // Create a simple input element for the autocomplete
        const inputElement = document.createElement('input');
        inputElement.type = 'text';
        inputElement.placeholder = 'Search for a location or address';
        inputElement.style.width = '100%';
        inputElement.style.padding = '8px';
        inputElement.style.boxSizing = 'border-box';
        inputElement.style.border = '1px solid transparent';
        inputElement.style.borderRadius = '4px';
        inputElement.style.outline = 'none';
        inputElement.style.fontSize = '14px';
        
        // Append the input to the container
        searchContainerRef.current.appendChild(inputElement);
        
        // Initialize the autocomplete on the input
        const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
          types: ['geocode', 'establishment'],
          fields: ['address_components', 'geometry', 'name', 'formatted_address', 'place_id']
        });
        
        // Add listener for place selection
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place && place.geometry && place.geometry.location) {
            handlePlaceSelection(place);
          } else {
            console.warn('Place selected but no geometry found:', place);
          }
        });
        
        // Focus the input automatically
        setTimeout(() => {
          inputElement.focus();
        }, 100);
      }
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
      setGeocodingError('Error initializing location search');
    }
  };
  
  // Handle place selection from either API
  const handlePlaceSelection = (place: google.maps.places.PlaceResult) => {
    const location = extractLocationFromPlace(place);
    
    // Update form fields
    setSublocation(location.sublocation || '');
    setStreetAddress(location.street_address || '');
    setCity(location.city || '');
    setState(location.state || '');
    setCountry(location.country || '');
    
    // Set the full address (hidden field)
    setAddress(location.address || place.formatted_address || '');
    
    // Update map
    if (location.geolocation) {
      setCurrentGeolocation(location.geolocation);
      // Use a higher zoom level for better detail
      setMapImageUrl(generateMapboxStaticImageUrl(
        location.geolocation,
        500,  // width
        300,  // height
        16    // zoom level
      ));
    }
    
    // Store external identifiers if available
    if (place.place_id) {
      const googleMapsIdentifier = {
        provider: 'google_maps' as const,
        id: place.place_id
      };
      
      setExternalIdentifiers([googleMapsIdentifier]);
    }
    
    // Log detailed place information
    console.log('Selected Place Details:', {
      name: place.name,
      formatted_address: place.formatted_address,
      city: location.city,
      state: location.state,
      country: location.country,
      latitude: location.geolocation?.latitude,
      longitude: location.geolocation?.longitude,
      place_id: place.place_id
    });
  };

  const handleSave = () => {
    // Construct the location object from the form fields
    const updatedLocation: ShoeboxLocation = {
      sublocation: sublocation || undefined,
      address: address || undefined,
      street_address: streetAddress || undefined,
      city: city || undefined,
      state: state || undefined,
      country: country || undefined,
      geolocation: currentGeolocation || undefined,
      external_identifiers: externalIdentifiers
    };
    
    // If the image is part of an event, show the modal to ask what to update
    if (hasEvent && onSaveEventLocation && onSaveLocation) {
      setPendingLocation(updatedLocation);
      setModalOpen(true);
      return; // Don't proceed with saving until user chooses from modal
    }
    
    // Handle both null and undefined for location checks
    const hasImageLocation = location !== null && location !== undefined;
    const hasEventLocation = eventLocation !== null && eventLocation !== undefined;
    
    // If not part of an event, use the original logic
    if (!hasImageLocation && hasEventLocation && onSaveEventLocation) {
      // If image has no location but event does, update event location
      onSaveEventLocation(updatedLocation);
    } else if (hasImageLocation && !hasEventLocation && onSaveLocation) {
      // If image has location but event doesn't, update image location
      onSaveLocation(updatedLocation);
    } else if (hasImageLocation && hasEventLocation) {
      // If both have location, default to updating image location
      if (onSaveLocation) {
        onSaveLocation(updatedLocation);
      }
    } else if (onSaveLocation) {
      // Default case: update image location
      onSaveLocation(updatedLocation);
    }
    
    setEditing(false);
  };
  
  // Handle saving to just this image
  const handleSaveToImage = () => {
    if (pendingLocation && onSaveLocation) {
      onSaveLocation(pendingLocation);
      setModalOpen(false);
      setEditing(false);
      setPendingLocation(null);
    }
  };
  
  // Handle saving to the event (which affects all images)
  const handleSaveToEvent = () => {
    if (pendingLocation && onSaveEventLocation) {
      onSaveEventLocation(pendingLocation);
      setModalOpen(false);
      setEditing(false);
      setPendingLocation(null);
    }
  };

  const handleCancel = () => {
    // Handle both null and undefined for location checks
    const hasImageLocation = location !== null && location !== undefined;
    const hasEventLocation = eventLocation !== null && eventLocation !== undefined;
    
    // Reset form values based on which location is available
    if (hasImageLocation) {
      setSublocation(location.sublocation || '');
      setAddress(location.address || '');
      setStreetAddress(location.street_address || '');
      setCity(location.city || '');
      setState(location.state || '');
      setCountry(location.country || '');
      setExternalIdentifiers(location.external_identifiers || []);
    } else if (hasEventLocation) {
      setSublocation(eventLocation.sublocation || '');
      setAddress(eventLocation.address || '');
      setStreetAddress(eventLocation.street_address || '');
      setCity(eventLocation.city || '');
      setState(eventLocation.state || '');
      setCountry(eventLocation.country || '');
      setExternalIdentifiers(eventLocation.external_identifiers || []);
    } else {
      // Reset to empty values if no location data is available
      setSublocation('');
      setAddress('');
      setStreetAddress('');
      setCity('');
      setState('');
      setCountry('');
      setExternalIdentifiers([]);
    }
    setEditing(false);
  };

  return (
    <Stack gap={0} style={{flex: 1}}>
      {/* Modal for asking if location changes should apply to all event images */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={<Title order={4}>Apply Location Change</Title>}
        centered
      >
        <Stack gap="md">
          <Text>Would you like to apply this location change to:</Text>
          
          <Button fullWidth onClick={handleSaveToImage} variant="outline">
            Just this image
          </Button>
          
          <Button fullWidth onClick={handleSaveToEvent} color="blue">
            All images in this event
          </Button>
          
          <Button fullWidth onClick={() => setModalOpen(false)} variant="subtle" color="gray">
            Cancel
          </Button>
        </Stack>
      </Modal>
      
      <Text mt="sm" fw={500} size={'sm'}>Location</Text>
      
      {!editing ? (
        <Paper mt={5} withBorder radius="sm" style={{overflow: 'hidden'}}>
          <Group wrap="nowrap" align="stretch" style={{height: 90}}>
            <Box style={{width: '33%', overflow: 'hidden'}}>
              {isGeocodingLoading ? (
                <Box style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa'}}>
                  <IconLoader2 size={24} className="mantine-spin" style={{color: '#868e96'}} />
                </Box>
              ) : mapImageUrl ? (
                <img
                  src={mapImageUrl}
                  alt={"location map"}
                  style={{width: '100%', height: '100%', objectFit: 'cover'}}
                />
              ) : (
                <Box style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa'}}>
                  <IconMapPinQuestion size={20} style={{color: '#868e96'}} />
                  {geocodingError && <Text size="xs" c="dimmed" style={{marginLeft: 8}}>{geocodingError}</Text>}
                </Box>
              )}
            </Box>
            <Stack gap={2} p="sm" justify="center" style={{width: '67%'}}>
              <Text size="sm" fw={500}>
                {(location?.sublocation || location?.street_address || eventLocation?.sublocation || eventLocation?.street_address) ? 
                  (location?.sublocation || location?.street_address || eventLocation?.sublocation || eventLocation?.street_address) : 
                  'Unknown Location'}
              </Text>
              {(location?.city || location?.state || location?.country || eventLocation?.city || eventLocation?.state || eventLocation?.country) ? (
                <>
                  <Text size="sm" c="dimmed">
                    {location?.city || eventLocation?.city || ''}
                    {(location?.state || eventLocation?.state) ? 
                      ((location?.city || eventLocation?.city) ? ', ' : '') + (location?.state || eventLocation?.state) : 
                      ''}
                  </Text>
                  {(location?.country || eventLocation?.country) && 
                    <Text size="sm" c="dimmed">{location?.country || eventLocation?.country}</Text>}
                </>
              ) : null}
            </Stack>
            <ActionIcon variant="subtle" onClick={() => setEditing(true)}>
              <IconPencil size={16}/>
            </ActionIcon>
          </Group>
        </Paper>
      ) : (
        <Paper mt={5} withBorder radius="sm" style={{overflow: 'hidden'}}>
          <Group wrap="nowrap" align="stretch" style={{height: 90}}>
            <Box style={{width: '33%', overflow: 'hidden'}}>
              <Box style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa'}}>
                <IconMapPinQuestion size={20} style={{color: '#868e96'}} />
              </Box>
            </Box>
            <Stack gap={2} p="sm" justify="center" style={{width: '67%'}}>
              <Text size="sm" fw={500}>Location Unknown</Text>
              <Text size="xs" c="dimmed">No location data available for this image</Text>
            </Stack>
            <ActionIcon variant="subtle" onClick={() => setEditing(true)}>
              <IconPencil size={16}/>
            </ActionIcon>
          </Group>
        </Paper>
      )}
      
      {editing && (
        <Paper withBorder p="md" radius="md">
          <Stack gap="md">
            {/* Google Maps Autocomplete */}
            <Box>
              {/* <Text size="sm" fs='italic' mb={5}>Search for a location or address</Text> */}
              {isGoogleMapsLoading ? (
                <TextInput
                  placeholder="Search for a location..."
                  disabled
                  rightSection={<IconSearch size={16} style={{ opacity: 0.5 }} />}
                  style={{ minHeight: 40 }}
                />
              ) : (
                <Box ref={searchContainerRef} style={{ minHeight: 40, border: '1px solid #ced4da', borderRadius: '4px', padding: '0' }} />
              )}

              <Text size="xs" c="dimmed" mt={5} fs="italic">Start typing to see suggestions from Google Maps</Text>
              {geocodingError && (
                <Text size="xs" c="red" mt={5}>{geocodingError}</Text>
              )}
            </Box>
            
            <Divider label="OR" labelPosition="center" />
            
            {/* Manual Location Entry */}
            <Box>
              {/* <Text size="sm" fw={500} mb={5}>Enter location details manually</Text> */}
              <Stack gap="xs">
                  <TextInput
                    label="Building/Landmark"
                    placeholder="e.g. Eiffel Tower"
                    value={sublocation}
                    onChange={(e) => setSublocation(e.currentTarget.value)}
                  />
                  <TextInput
                    label="Street Address"
                    placeholder="e.g. 5 Avenue Anatole France"
                    value={streetAddress}
                    onChange={(e) => {
                      setStreetAddress(e.currentTarget.value);
                      // Update the full address as well (hidden field)
                      const fullAddress = `${e.currentTarget.value}${city ? `, ${city}` : ''}${state ? `, ${state}` : ''}${country ? `, ${country}` : ''}`;
                      setAddress(fullAddress);
                    }}
                  />
                  <TextInput
                    label="City"
                    placeholder="e.g. Paris"
                    value={city}
                    onChange={(e) => setCity(e.currentTarget.value)}
                  />
                  <TextInput
                    label="State/Province"
                    placeholder="e.g. Île-de-France"
                    value={state}
                    onChange={(e) => setState(e.currentTarget.value)}
                  />
                  <TextInput
                    label="Country"
                    placeholder="e.g. France"
                    value={country}
                    onChange={(e) => setCountry(e.currentTarget.value)}
                  />
                </Stack>
              </Box>
            
            <Group justify="flex-end" mt="xs">
              <Button 
                variant="outline" 
                size="xs" 
                leftSection={<IconX size={14} />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button 
                size="xs" 
                leftSection={<IconCheck size={14} />}
                onClick={handleSave}
              >
                Save
              </Button>
            </Group>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};
